import {
  ThresholdDashboardEntry,
  WorkflowDashboardEntry,
  WORKFLOW_STEP_STATUS_ENUM,
  WorkflowStatusBucket,
  StatusBucket,
  // WORKFLOW_STATUS_ENUM,
} from "../api/Workflows";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { classNames, showDateUTCMMDD } from "../utils";
import DashboardEntry, { ReportEntryStyle } from "./DashboardEntry";

interface TableProps {
  data?: WorkflowDashboardEntry[] | undefined;
  threshold?: ThresholdDashboardEntry[] | undefined;
  header: StatusBucket[];
  days: number;
  total?: boolean;
  edit?: boolean;
  isLoading: boolean;
  defaultThreshold?: number;
  setDashboardThreshold?: (threshold: {
    [x: string]: ThresholdDashboardEntry;
  }) => void;
}

const Table = ({
  data,
  threshold,
  header,
  isLoading = true,
  total = false,
  defaultThreshold = 100,
}: TableProps) => {
  const filterData = (
    data: WorkflowDashboardEntry[]
  ): WorkflowDashboardEntry[] => {
    // List of unsupported step names that don't have corresponding table columns
    const unsupportedStepNames = [
      "360",
      "360PlusReview",
      "QA",
      "ScrapeProcess",
    ];

    return data.filter((value) => {
      if (value.step_name === null) return false;

      if (
        typeof value.step_name === "string" &&
        unsupportedStepNames.includes(value.step_name)
      ) {
        return false;
      }

      return true;
    });
  };

  const initializeDate = () => {
    const filteredData = filterData(data ?? []);
    const datesNotDue = filteredData.filter((entry) => !entry.past_due);
    const dates: Array<Date> = [];
    datesNotDue.forEach((date) => {
      if (!dates.includes(date.date)) dates.push(date.date);
    });
    return dates.sort(
      (a: Date, b: Date) => new Date(a).getTime() - new Date(b).getTime()
    );
  };

  const dates = initializeDate();

  const isToday = (due: Date | string): boolean => {
    const dueDate = new Date(due);
    const today = new Date();

    return (
      today.getUTCDate() === dueDate.getUTCDate() &&
      today.getUTCMonth() === dueDate.getUTCMonth()
    );
  };

  const isStepInHeaders = (
    stepName: WORKFLOW_STEP_STATUS_ENUM | null
  ): boolean => {
    return header.some((value) => value.step_names?.includes(stepName));
  };

  const countGridPastDueValue = (
    stepName?: (null | WORKFLOW_STEP_STATUS_ENUM)[]
  ): number => {
    if (!data) return 0;
    let result = filterData(data).reduce((initial, value) => {
      if (value.past_due && stepName?.includes(value.step_name)) {
        return initial + value.count;
      }
      return initial;
    }, 0);

    return result;
  };

  const countGridPastDueTotal = (): number => {
    if (!data) return 0;
    return filterData(data).reduce((initial, value) => {
      if (value.past_due && isStepInHeaders(value.step_name)) {
        return initial + value.count;
      }
      return initial;
    }, 0);
  };

  const countGridValue = (
    day: Date,
    stepName?: (null | WORKFLOW_STEP_STATUS_ENUM)[]
  ): number => {
    if (!data) return 0;
    return filterData(data).reduce((initial, value) => {
      if (
        day === value.date &&
        stepName?.includes(value.step_name) &&
        !value.past_due
      ) {
        return initial + value.count;
      }
      return initial;
    }, 0);
  };

  const countRow = (day: Date): number => {
    if (!data) return 0;
    const filteredData = filterData(data);
    return filteredData.reduce((initial, value) => {
      if (
        day === value.date &&
        isStepInHeaders(value.step_name) &&
        !value.past_due
      ) {
        return initial + value.count;
      }
      return initial;
    }, 0);
  };

  const countColumn = (headerName: string): number => {
    if (!data) return 0;
    return filterData(data).reduce((initial, value) => {
      if (
        WorkflowStatusBucket.some(
          (bucket) =>
            bucket.step_names.includes(value.step_name) &&
            bucket.name === headerName
        )
      ) {
        return initial + value.count;
      }
      return initial;
    }, 0);
  };

  const totalCount = () => {
    if (!data) return 0;
    return filterData(data).reduce((initial, value) => {
      if (isStepInHeaders(value.step_name)) {
        return initial + value.count;
      }
      return initial;
    }, 0);
  };

  const getEarliestPastDueDate = (): Date => {
    if (!data) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday;
    }

    const pastDueEntries = filterData(data).filter((entry) => entry.past_due);

    if (pastDueEntries.length === 0) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday;
    }

    return pastDueEntries
      .map((e) => e.date)
      .reduce((earliest, current) => (current < earliest ? current : earliest));
  };

  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          <div className="flex rounded-t-xl justify-center items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1">
            Due Date
          </div>
          {header.map((header) => (
            <div
              className={classNames(
                "flex text-center justify-center items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1",
                header.is_manual ? "bg-slate-200" : ""
              )}
              key={header.name}
            >
              {header.name}
            </div>
          ))}
          {total && (
            <div className="flex rounded-t-xl items-center justify-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1">
              Total
            </div>
          )}
        </div>
        <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
          {!isLoading && countGridPastDueTotal() > 0 && (
            <div
              className={classNames(
                "flex border-b text-xs leading-4 font-normal h-12 text-gray-500"
              )}
              key={`dayrow-past`}
            >
              <DashboardEntry
                text={"Past Due"}
                key={`daycount-pastdue`}
                style={ReportEntryStyle.BOLD_TEXT}
              />

              {header.map((value) => (
                <DashboardEntry
                  text={countGridPastDueValue(value.step_names).toString()}
                  key={`${value.name}-past-due`}
                  day={getEarliestPastDueDate()}
                  status={value.name}
                  isManual={value.is_manual}
                  clickable={true}
                />
              ))}

              {total && (
                <DashboardEntry
                  text={countGridPastDueTotal().toString()}
                  key={`total-past-due`}
                  style={ReportEntryStyle.BOLD_TEXT}
                />
              )}
            </div>
          )}
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            dates.map((day, index) => (
              <div
                className={classNames(
                  "flex border-b text-xs leading-4 font-normal h-12 text-gray-500"
                )}
                key={`dayrow-${day}`}
              >
                <DashboardEntry
                  text={isToday(day) ? "Today" : showDateUTCMMDD(day)}
                  key={`daycount-${index}`}
                  style={ReportEntryStyle.BOLD_TEXT}
                />

                {header.map((value) => (
                  <DashboardEntry
                    text={countGridValue(day, value.step_names).toString()}
                    key={`${value.name}-${index}`}
                    day={day}
                    status={value.name}
                    isManual={value.is_manual}
                    clickable={true}
                  />
                ))}

                {total && (
                  <DashboardEntry
                    text={countRow(day).toString()}
                    key={`total-${day}`}
                    style={ReportEntryStyle.BOLD_TEXT}
                  />
                )}
              </div>
            ))
          )}
          {!isLoading && total && (
            <div
              className={classNames(
                "flex border-b text-xs leading-4 font-normal h-12 text-gray-500"
              )}
              key={`totalrow`}
            >
              <DashboardEntry
                text="TOTAL"
                key="total-row"
                style={ReportEntryStyle.BOLD_TEXT}
              />

              {header.map((value, index) => (
                <DashboardEntry
                  text={countColumn(value.name).toString()}
                  key={`${value.name}-${index}`}
                  style={ReportEntryStyle.BOLD_TEXT}
                  isManual={value.is_manual}
                />
              ))}

              <DashboardEntry
                text={totalCount().toString()}
                key="`total-total"
                style={ReportEntryStyle.BOLD_TEXT}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Table;
